/**
 * BenutzerAnlegen: admin page at /benutzer-anlegen (enterprise).
 *
 * An admin creates a new user. Section 3 offers to carry the admin's own AI
 * and alerting settings over to the new account via inheritAi/inheritAlerting
 * toggles submitted with the request; section 4 shares the admin's own spaces
 * and instances with the new user right away. Design: Integration-in-One
 * tokens, all ba-* classes scoped under .ba-root so nothing collides with
 * the app CSS.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";

const CSS = `
.ba-root{ color:var(--iio-text-1); }
.ba-ar{ color:var(--iio-signal); font-weight:700; }

.ba-ph{ margin-bottom:var(--iio-s-6); }
.ba-ph .crumb{ font-size:11px; letter-spacing:.04em; color:var(--iio-text-3); margin-bottom:7px; }
.ba-ph h1{ margin:0; font-size:27px; font-weight:700; letter-spacing:-.02em; }
.ba-ph .sub{ margin-top:6px; font-size:13.5px; color:var(--iio-text-2); max-width:600px; line-height:1.5; }

.ba-grid{ display:grid; grid-template-columns:1fr 372px; gap:var(--iio-s-6); align-items:start; }
@media(max-width:1180px){ .ba-grid{ grid-template-columns:1fr; } }

.ba-sect{ background:var(--iio-bg-2); border:1px solid var(--iio-border); border-radius:var(--iio-r-xl); padding:var(--iio-s-5) var(--iio-s-6); margin-bottom:var(--iio-s-4); box-shadow:var(--iio-shadow-sm); opacity:0; transform:translateY(14px); animation:baRise .5s cubic-bezier(.22,.61,.36,1) forwards; }
.ba-sect:nth-of-type(1){animation-delay:.04s} .ba-sect:nth-of-type(2){animation-delay:.11s} .ba-sect:nth-of-type(3){animation-delay:.18s} .ba-sect:nth-of-type(4){animation-delay:.25s}
@keyframes baRise{ to{ opacity:1; transform:none; } }
.ba-sh{ display:flex; align-items:center; gap:11px; margin-bottom:var(--iio-s-5); }
.ba-no{ width:25px; height:25px; border-radius:50%; border:1.5px solid var(--iio-brand-ink); color:var(--iio-brand-ink); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
[data-theme="dark"] .ba-root .ba-no{ border-color:var(--iio-info); color:var(--iio-info); }
.ba-sh h2{ margin:0; font-size:15.5px; font-weight:700; }
.ba-sh .shsub{ font-size:11.5px; color:var(--iio-text-3); margin-top:1px; }
.ba-soon{ margin-left:auto; font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--iio-warning); display:inline-flex; align-items:center; gap:6px; }
.ba-soon::before{ content:''; width:6px; height:6px; border-radius:50%; background:currentColor; }

.ba-row2{ display:grid; grid-template-columns:1fr 1fr; gap:var(--iio-s-4); }
.ba-field{ display:flex; flex-direction:column; gap:6px; margin-bottom:var(--iio-s-4); }
.ba-field:last-child{ margin-bottom:0; }
.ba-field label{ font-size:10.5px; font-weight:600; letter-spacing:.1em; text-transform:uppercase; color:var(--iio-text-3); }
.ba-input{ height:38px; padding:0 var(--iio-s-3); border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-1); font-size:14px; outline:none; transition:border-color var(--iio-tx-fast),box-shadow var(--iio-tx-fast); width:100%; color:var(--iio-text-1); font-family:inherit; }
.ba-input:focus{ border-color:var(--iio-brand-ink); box-shadow:0 0 0 3px rgba(10,61,74,.12); background:var(--iio-bg-2); }
[data-theme="dark"] .ba-root .ba-input:focus{ box-shadow:0 0 0 3px rgba(90,154,224,.18); }
.ba-hint{ font-size:11px; color:var(--iio-text-3); }
.ba-hint.ok{ color:var(--iio-success); }
.ba-hint.bad{ color:var(--iio-critical); }

.ba-seg{ display:inline-flex; border:1px solid var(--iio-border); border-radius:var(--iio-r-md); padding:3px; background:var(--iio-bg-1); gap:2px; }
.ba-seg button{ border:0; background:transparent; color:var(--iio-text-2); font-size:13px; font-weight:600; padding:7px 16px; border-radius:var(--iio-r-sm); cursor:pointer; transition:all var(--iio-tx-fast); font-family:inherit; }
.ba-seg button.on{ background:var(--iio-brand-ink); color:#fff; }

.ba-pw{ display:flex; gap:8px; align-items:center; }
.ba-pw .val{ flex:1; height:38px; display:flex; align-items:center; padding:0 var(--iio-s-3); border:1px dashed var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-1); font-size:15px; font-weight:600; color:var(--iio-text-1); }
.ba-ibtn{ height:38px; padding:0 12px; border:1px solid var(--iio-border); background:var(--iio-bg-2); border-radius:var(--iio-r-sm); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:7px; font-size:12.5px; font-weight:600; color:var(--iio-text-2); transition:all var(--iio-tx-fast); font-family:inherit; }
.ba-ibtn:hover{ background:var(--iio-bg-3); color:var(--iio-text-1); }
.ba-ibtn svg{ width:15px; height:15px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }

.ba-tr{ display:flex; align-items:flex-start; gap:13px; padding:13px; border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-1); margin-bottom:10px; transition:border-color var(--iio-tx-fast),background var(--iio-tx-fast); }
.ba-tr:last-child{ margin-bottom:0; }
.ba-tr.on{ border-color:var(--iio-brand-ink); background:var(--iio-bg-2); }
[data-theme="dark"] .ba-root .ba-tr.on{ border-color:var(--iio-info); }
.ba-tr.dis{ opacity:.62; }
.ba-tgl{ width:42px; height:24px; border-radius:13px; background:var(--iio-bg-3); border:1px solid var(--iio-border); position:relative; cursor:pointer; flex-shrink:0; margin-top:1px; transition:background var(--iio-tx-fast),border-color var(--iio-tx-fast); }
.ba-tgl::after{ content:''; position:absolute; top:2px; left:2px; width:18px; height:18px; border-radius:50%; background:var(--iio-text-3); transition:transform var(--iio-tx-fast),background var(--iio-tx-fast); }
.ba-tgl.on{ background:var(--iio-brand-ink); border-color:var(--iio-brand-ink); }
.ba-tgl.on::after{ transform:translateX(18px); background:#fff; }
.ba-tr.dis .ba-tgl{ cursor:not-allowed; }
.ba-tm{ flex:1; min-width:0; }
.ba-tm .t{ font-size:13.5px; font-weight:600; display:flex; align-items:center; gap:8px; }
.ba-tm .d{ font-size:11.5px; color:var(--iio-text-3); margin-top:2px; line-height:1.45; }
.ba-prov{ display:inline-flex; align-items:center; gap:6px; margin-top:8px; font-size:11px; color:var(--iio-text-2); }
.ba-prov .dot{ width:7px; height:7px; border-radius:50%; background:var(--iio-success); }
.ba-prov.off .dot{ background:var(--iio-text-4); }
.ba-mini{ font-size:9.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--iio-warning); }

.ba-fb{ margin:2px 0 var(--iio-s-4); padding:12px 14px; border:1px dashed var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-1); display:flex; align-items:center; gap:14px; }
.ba-fn{ display:flex; align-items:center; gap:9px; }
.ba-fa{ width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
.ba-fa.admin{ background:var(--iio-brand-ink); color:#fff; }
.ba-fa.usr{ background:var(--iio-bg-3); color:var(--iio-text-3); border:1px dashed var(--iio-border); }
.ba-fa.usr.live{ border-style:solid; }
.ba-fn .nm{ font-size:12px; font-weight:600; line-height:1.1; }
.ba-fn .rl{ font-size:10px; color:var(--iio-text-3); }
.ba-ff{ flex:1; display:flex; align-items:center; justify-content:center; gap:5px; color:var(--iio-border); font-weight:700; font-size:16px; }
.ba-ff.live{ color:var(--iio-signal); }
.ba-ff .c{ opacity:.3; }
.ba-ff.live .c{ animation:baDash 1.3s ease-in-out infinite; }
.ba-ff.live .c:nth-child(2){ animation-delay:.18s; } .ba-ff.live .c:nth-child(3){ animation-delay:.36s; }
@keyframes baDash{ 0%,100%{opacity:.25;transform:translateX(-2px)} 50%{opacity:1;transform:translateX(2px)} }

.ba-sub{ font-size:10px; letter-spacing:.14em; text-transform:uppercase; color:var(--iio-text-3); font-weight:700; margin:0 0 9px; }
.ba-srow{ display:flex; align-items:center; gap:12px; padding:11px 13px; border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-1); margin-bottom:9px; }
.ba-srow.dis{ opacity:.6; }
.ba-srow.on{ border-color:var(--iio-brand-ink); background:var(--iio-bg-2); }
[data-theme="dark"] .ba-root .ba-srow.on{ border-color:var(--iio-info); }
.ba-spc{ border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); margin-bottom:9px; overflow:hidden; }
.ba-spc-head{ display:flex; align-items:center; gap:12px; padding:11px 13px; background:var(--iio-bg-1); cursor:pointer; }
.ba-chev{ display:flex; color:var(--iio-text-3); transition:transform var(--iio-tx-fast); flex-shrink:0; }
.ba-chev svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.ba-chev.open{ transform:rotate(90deg); }
.ba-spc-body{ padding:4px 13px 10px 42px; background:var(--iio-bg-2); border-top:1px solid var(--iio-border-subtle); }
.ba-inst{ display:flex; align-items:center; gap:10px; padding:6px 0; cursor:pointer; font-size:13px; }
.ba-inst-n{ color:var(--iio-text-1); }
.ba-chk{ width:18px; height:18px; border-radius:var(--iio-r-sm); border:1.5px solid var(--iio-border); flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.ba-chk.on{ background:var(--iio-brand-ink); border-color:var(--iio-brand-ink); }
.ba-chk svg{ width:11px; height:11px; stroke:#fff; fill:none; stroke-width:3; stroke-linecap:round; stroke-linejoin:round; opacity:0; }
.ba-chk.on svg{ opacity:1; }
.ba-srico{ width:30px; height:30px; border-radius:var(--iio-r-md); background:var(--iio-bg-3); display:flex; align-items:center; justify-content:center; color:var(--iio-brand-ink); flex-shrink:0; }
[data-theme="dark"] .ba-root .ba-srico{ color:var(--iio-info); }
.ba-srico svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:1.7; stroke-linecap:round; stroke-linejoin:round; }
.ba-srm{ flex:1; min-width:0; }
.ba-srm .n{ font-size:13px; font-weight:600; }
.ba-srm .m{ font-size:11px; color:var(--iio-text-3); margin-top:1px; }
.ba-sel{ height:32px; padding:0 28px 0 10px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-2); font-size:12.5px; font-weight:600; color:var(--iio-text-2); appearance:none; font-family:inherit; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A8590' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 9px center; }
[data-theme="dark"] .ba-root .ba-sel{ background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236A7A82' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); }
.ba-sfoot{ font-size:11.5px; color:var(--iio-text-3); margin-top:6px; }

.ba-pv{ position:sticky; top:0; background:var(--iio-bg-2); border:1px solid var(--iio-border); border-radius:var(--iio-r-xl); box-shadow:var(--iio-shadow-md); overflow:hidden; opacity:0; transform:translateY(14px); animation:baRise .5s cubic-bezier(.22,.61,.36,1) .28s forwards; }
.ba-pvtop{ background:var(--iio-brand-ink); color:#fff; padding:var(--iio-s-5) var(--iio-s-5) var(--iio-s-6); position:relative; overflow:hidden; }
.ba-pvtop .gridbg{ position:absolute; inset:0; background-image:linear-gradient(rgba(245,242,236,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(245,242,236,.05) 1px,transparent 1px); background-size:26px 26px; }
.ba-pvcap{ position:relative; font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:var(--iio-signal-2); font-weight:600; margin-bottom:14px; }
.ba-pvid{ position:relative; display:flex; align-items:center; gap:13px; }
.ba-pvava{ width:52px; height:52px; border-radius:50%; background:rgba(255,255,255,.1); border:1.5px solid rgba(255,255,255,.35); display:flex; align-items:center; justify-content:center; font-size:19px; font-weight:700; flex-shrink:0; transition:transform var(--iio-tx-fast); }
.ba-pvava.pop{ animation:baPop .4s cubic-bezier(.22,1.2,.4,1); }
@keyframes baPop{ 0%{transform:scale(.82)} 60%{transform:scale(1.06)} 100%{transform:scale(1)} }
.ba-pvnm{ font-size:17px; font-weight:700; line-height:1.15; }
.ba-pvun{ font-size:12.5px; color:var(--iio-hero-text-2); margin-top:2px; }
.ba-pvrole{ position:relative; margin-top:13px; display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:600; color:#fff; }
.ba-pvrole .bar{ width:2px; height:13px; background:var(--iio-signal); }
.ba-pvbody{ padding:var(--iio-s-5); }
.ba-recv{ list-style:none; margin:0 0 var(--iio-s-5); padding:0; }
.ba-recv li{ display:flex; align-items:center; gap:11px; padding:9px 2px; border-bottom:1px solid var(--iio-border-subtle); font-size:13px; }
.ba-recv li:last-child{ border-bottom:0; }
.ba-recv .ic{ width:24px; height:24px; border-radius:var(--iio-r-sm); display:flex; align-items:center; justify-content:center; flex-shrink:0; background:var(--iio-bg-3); color:var(--iio-text-3); transition:all var(--iio-tx-fast); }
.ba-recv .ic svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.ba-recv li.on .ic{ background:rgba(45,138,90,.13); color:var(--iio-success); }
.ba-recv .tx{ flex:1; color:var(--iio-text-2); }
.ba-recv li.on .tx{ color:var(--iio-text-1); font-weight:600; }
.ba-recv .st{ font-size:11.5px; font-weight:600; color:var(--iio-text-4); }
.ba-recv li.on .st{ color:var(--iio-success); }
.ba-cta{ display:flex; flex-direction:column; gap:9px; }
.ba-btn{ height:44px; border:1px solid transparent; border-radius:var(--iio-r-md); font-size:14px; font-weight:700; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:9px; transition:all var(--iio-tx-fast); font-family:inherit; }
.ba-btn.prim{ background:var(--iio-signal); color:#fff; }
.ba-btn.prim:hover{ background:var(--iio-signal-2); }
.ba-btn.prim:disabled{ opacity:.55; cursor:not-allowed; }
.ba-btn.ghost{ background:transparent; color:var(--iio-text-2); border-color:var(--iio-border); }
.ba-btn.ghost:hover{ background:var(--iio-bg-3); color:var(--iio-text-1); }
.ba-btn svg{ width:17px; height:17px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }

.ba-denied{ max-width:440px; margin:80px auto; text-align:center; color:var(--iio-text-2); }
.ba-denied h2{ font-size:20px; color:var(--iio-text-1); margin:0 0 8px; }

@media(prefers-reduced-motion:reduce){ .ba-root *{ animation:none!important; } .ba-sect,.ba-pv{ opacity:1; transform:none; } }
`;

const genPw = () => {
  const A = "ABCDEFGHJKLMNPQRSTUVWXYZ", B = "abcdefghijkmnpqrstuvwxyz", C = "23456789";
  const r = s => s[Math.floor(Math.random() * s.length)];
  const seg = () => r(A) + r(B) + r(B) + r(C);
  return seg() + "-" + seg() + "-" + r(A) + r(C) + r(B);
};
// Parameter deliberately not named "t": that name is the translator function in this file.
const initials = txt => {
  const p = (txt || "").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || p[0]?.[1] || "")).toUpperCase() || "?";
};

export default function BenutzerAnlegen({ user, spaces = [], onDone }) {
  const { t } = useTranslation(["benutzer", "gemeinsam"]);
  const { toasts, addToast } = useToast();
  const [name, setName] = useState("");
  const [uname, setUname] = useState("");
  const [mail, setMail] = useState("");
  const [role, setRole] = useState("user");
  const [pw, setPw] = useState(() => genPw());
  const [forcePw, setForcePw] = useState(true);
  const [inheritAi, setInheritAi] = useState(false);
  const [inheritAlert, setInheritAlert] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pop, setPop] = useState(false);
  const [shareSel, setShareSel] = useState({}); // { [spaceId]: { role:"read"|"admin", insts:[instanceId] } }
  const [instCache, setInstCache] = useState({}); // { [spaceId]: [{id,name}] }
  const [expanded, setExpanded] = useState({}); // { [spaceId]: bool }

  useEffect(() => { api("/api/auth/settings").then(setAiInfo).catch(() => {}); }, []);

  const adminIni = initials(user?.displayName || user?.username || "Admin");
  const aiConfigured = !!(aiInfo && (aiInfo.hasBedrockKey || aiInfo.hasApiKey));
  const aiLabel = aiInfo
    ? (aiInfo.llmProvider === "anthropic"
        ? (aiInfo.hasApiKey ? "Anthropic API" : t("anlegen.vererben.keinSchluessel", { anbieter: "Anthropic" }))
        : (aiInfo.hasBedrockKey ? `Amazon Bedrock (${aiInfo.bedrockRegion || "eu-central-1"})` : t("anlegen.vererben.keinSchluessel", { anbieter: "Amazon Bedrock" })))
    : t("gemeinsam:zustand.laedt");

  // The admin's personal alerting config (email/SMTP + ServiceNow). The global SMTP setting does not count here, that one applies to everyone.
  const alertConfigured = !!(aiInfo && (aiInfo.smtpHost || aiInfo.snowUrl));
  const alertParts = aiInfo ? [aiInfo.smtpHost ? t("anlegen.vererben.alertingTeilEmail") : null, aiInfo.snowUrl ? "ServiceNow" : null].filter(Boolean) : [];
  const alertLabel = alertParts.length ? t("anlegen.vererben.alertingHinterlegt", { teile: alertParts.join(" + ") }) : t("anlegen.vererben.alertingFehlt");

  const ini = initials(name);
  const flowLive = (inheritAi && aiConfigured) || (inheritAlert && alertConfigured);

  const onName = v => { setName(v); setPop(true); setTimeout(() => setPop(false), 420); };
  const toggleAi = () => { if (aiConfigured) setInheritAi(v => !v); };
  const toggleAlert = () => { if (alertConfigured) setInheritAlert(v => !v); };

  // Sharing: only spaces the admin owns (role "owner"). Individual instances can be picked per space.
  const ownSpaces = (spaces || []).filter(s => s.role === "owner");
  const sharedSpaceCount = Object.values(shareSel).filter(v => v.insts && v.insts.length).length;
  const sharedInstCount = Object.values(shareSel).reduce((n, v) => n + ((v.insts && v.insts.length) || 0), 0);
  const loadInstances = async (spId) => {
    if (instCache[spId]) return;
    try { const list = await api(`/api/spaces/${spId}/instances`); setInstCache(c => ({ ...c, [spId]: Array.isArray(list) ? list : [] })); }
    catch { setInstCache(c => ({ ...c, [spId]: [] })); }
  };
  const toggleExpand = (spId) => { setExpanded(e => ({ ...e, [spId]: !e[spId] })); if (!instCache[spId]) loadInstances(spId); };
  const toggleInst = (spId, iid) => setShareSel(prev => {
    const cur = prev[spId] || { role: "read", insts: [] };
    const insts = cur.insts.includes(iid) ? cur.insts.filter(x => x !== iid) : [...cur.insts, iid];
    const next = { ...prev };
    if (insts.length) next[spId] = { ...cur, insts }; else delete next[spId];
    return next;
  });
  const toggleAllInsts = (spId, allIds) => setShareSel(prev => {
    const cur = prev[spId] || { role: "read", insts: [] };
    const allSel = allIds.length && allIds.every(id => cur.insts.includes(id));
    const next = { ...prev };
    if (allSel) delete next[spId]; else next[spId] = { ...cur, insts: [...allIds] };
    return next;
  });
  const setSpaceRole = (spId, r) => setShareSel(prev => prev[spId] ? { ...prev, [spId]: { ...prev[spId], role: r } } : prev);

  const valid = uname.trim().length >= 3 && pw.length >= 6;

  const submit = async () => {
    if (!valid) { addToast(t("anlegen.meldung.pflicht"), "error"); return; }
    setBusy(true);
    try {
      const res = await api("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({
          username: uname.trim(), password: pw, displayName: name.trim() || uname.trim(),
          email: mail.trim(), role, mustChangePassword: forcePw,
          inheritAi: inheritAi && aiConfigured, inheritAlerting: inheritAlert && alertConfigured,
          shares: Object.entries(shareSel).flatMap(([spId, sel]) => (sel.insts || []).map(iid => ({ type: "instance", id: iid, role: sel.role }))),
        }),
      });
      const inh = (res && res.inherited && res.inherited.length) ? ` (${res.inherited.join(", ")})` : "";
      addToast(t("anlegen.meldung.angelegt", { name: uname.trim().toLowerCase(), zusatz: inh }), "success");
      setName(""); setUname(""); setMail(""); setRole("user"); setPw(genPw()); setInheritAi(false); setInheritAlert(false); setShareSel({});
    } catch (e) {
      addToast(e.message || t("anlegen.meldung.fehlgeschlagen"), "error");
    }
    setBusy(false);
  };

  if (user && user.role !== "admin") {
    return (
      <div className="ba-root">
        <style>{CSS}</style>
        <div className="ba-denied">
          <h2>{t("anlegen.keinZugriff.titel")}</h2>
          <p>{t("anlegen.keinZugriff.text")}</p>
        </div>
        <ToastContainer toasts={toasts} />
      </div>
    );
  }

  return (
    <div className="ba-root">
      <style>{CSS}</style>

      <div className="ba-ph">
        <div className="crumb">{t("anlegen.kopf.bereich")} <span className="ba-ar">&gt;&gt;</span> {t("anlegen.kopf.gruppe")} <span className="ba-ar">&gt;&gt;</span> {t("anlegen.kopf.seite")}</div>
        <h1>{t("anlegen.kopf.titel")}</h1>
        <div className="sub">{t("anlegen.kopf.untertitel")}</div>
      </div>

      <div className="ba-grid">
        {/* LEFT */}
        <div>
          {/* 1 Identity */}
          <section className="ba-sect">
            <div className="ba-sh"><div className="ba-no">1</div><div><h2>{t("anlegen.identitaet.titel")}</h2><div className="shsub">{t("anlegen.identitaet.untertitel")}</div></div></div>
            <div className="ba-row2">
              <div className="ba-field"><label>{t("anlegen.identitaet.anzeigename")}</label><input className="ba-input" value={name} onChange={e => onName(e.target.value)} placeholder={t("anlegen.identitaet.anzeigenamePlatzhalter")} /></div>
              <div className="ba-field"><label>{t("anlegen.identitaet.benutzername")}</label><input className="ba-input" value={uname} onChange={e => setUname(e.target.value)} placeholder={t("anlegen.identitaet.benutzernamePlatzhalter")} />
                <span className={"ba-hint " + (uname.length === 0 ? "" : uname.trim().length >= 3 ? "ok" : "bad")}>{uname.length === 0 ? t("anlegen.identitaet.pflichtfeld") : uname.trim().length >= 3 ? t("anlegen.identitaet.verfuegbar") : t("anlegen.identitaet.zuKurz")}</span>
              </div>
            </div>
            <div className="ba-field"><label>{t("anlegen.identitaet.email")}</label><input className="ba-input" value={mail} onChange={e => setMail(e.target.value)} placeholder={t("anlegen.identitaet.emailPlatzhalter")} /></div>
          </section>

          {/* 2 Access */}
          <section className="ba-sect">
            <div className="ba-sh"><div className="ba-no">2</div><div><h2>{t("anlegen.zugang.titel")}</h2><div className="shsub">{t("anlegen.zugang.untertitel")}</div></div></div>
            <div className="ba-field"><label>{t("anlegen.zugang.rolle")}</label>
              <div className="ba-seg">
                <button type="button" className={role === "user" ? "on" : ""} onClick={() => setRole("user")}>{t("anlegen.zugang.rolleNutzer")}</button>
                <button type="button" className={role === "admin" ? "on" : ""} onClick={() => setRole("admin")}>{t("anlegen.zugang.rolleAdministrator")}</button>
              </div>
            </div>
            <div className="ba-field"><label>{t("anlegen.zugang.passwort")}</label>
              <div className="ba-pw">
                <div className="val">{pw}</div>
                <button type="button" className="ba-ibtn" onClick={() => setPw(genPw())}><svg viewBox="0 0 24 24"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>{t("anlegen.zugang.neu")}</button>
                <button type="button" className="ba-ibtn" onClick={() => { navigator.clipboard?.writeText(pw); addToast(t("anlegen.meldung.passwortKopiert"), "success"); }}><svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>{t("anlegen.zugang.kopieren")}</button>
              </div>
              <span className="ba-hint">{t("anlegen.zugang.passwortHinweis")}</span>
            </div>
            <div className={"ba-tr" + (forcePw ? " on" : "")} onClick={() => setForcePw(v => !v)} style={{ cursor: "pointer" }}>
              <div className={"ba-tgl" + (forcePw ? " on" : "")}></div>
              <div className="ba-tm"><div className="t">{t("anlegen.zugang.erzwingen")}</div><div className="d">{t("anlegen.zugang.erzwingenText")}</div></div>
            </div>
          </section>

          {/* 3 Inheritance */}
          <section className="ba-sect">
            <div className="ba-sh"><div className="ba-no">3</div><div><h2>{t("anlegen.vererben.titel")}</h2><div className="shsub">{t("anlegen.vererben.untertitel")}</div></div></div>

            <div className="ba-fb">
              <div className="ba-fn"><div className="ba-fa admin">{adminIni}</div><div><div className="nm">{t("anlegen.vererben.quelle")}</div><div className="rl">{t("anlegen.vererben.quelleRolle")}</div></div></div>
              <div className={"ba-ff" + (flowLive ? " live" : "")}><span className="c">&gt;</span><span className="c">&gt;</span><span className="c">&gt;</span></div>
              <div className="ba-fn"><div className={"ba-fa usr" + (flowLive ? " live" : "")}>{ini}</div><div><div className="nm">{name || t("anlegen.vererben.neuerNutzer")}</div><div className="rl">{t("anlegen.vererben.neuerNutzer")}</div></div></div>
            </div>

            <div className={"ba-tr" + (inheritAi ? " on" : "") + (aiConfigured ? "" : " dis")} onClick={toggleAi} style={{ cursor: aiConfigured ? "pointer" : "not-allowed" }}>
              <div className={"ba-tgl" + (inheritAi ? " on" : "")}></div>
              <div className="ba-tm">
                <div className="t">{t("anlegen.vererben.ki")}</div>
                <div className="d">{t("anlegen.vererben.kiText")}</div>
                <div className={"ba-prov" + (aiConfigured ? "" : " off")}><span className="dot"></span>{aiConfigured ? t("anlegen.vererben.kiHinterlegt", { quelle: aiLabel }) : t("anlegen.vererben.kiFehlt")}</div>
              </div>
            </div>

            <div className={"ba-tr" + (inheritAlert ? " on" : "") + (alertConfigured ? "" : " dis")} onClick={toggleAlert} style={{ cursor: alertConfigured ? "pointer" : "not-allowed" }}>
              <div className={"ba-tgl" + (inheritAlert ? " on" : "")}></div>
              <div className="ba-tm">
                <div className="t">{t("anlegen.vererben.alerting")}</div>
                <div className="d">{t("anlegen.vererben.alertingText")}</div>
                <div className={"ba-prov" + (alertConfigured ? "" : " off")}><span className="dot"></span>{alertLabel}</div>
              </div>
            </div>
          </section>

          {/* 4 Sharing */}
          <section className="ba-sect">
            <div className="ba-sh"><div className="ba-no">4</div><div><h2>{t("anlegen.teilen.titel")}</h2><div className="shsub">{t("anlegen.teilen.untertitel")}</div></div></div>

            {ownSpaces.length === 0 ? (
              <div className="ba-hint">{t("anlegen.teilen.keineEigenenSpaces")}</div>
            ) : (
              <>
                {ownSpaces.map(sp => {
                  const sel = shareSel[sp.id];
                  const list = instCache[sp.id];
                  const exp = expanded[sp.id];
                  const selCount = (sel && sel.insts.length) || 0;
                  return (
                    <div className="ba-spc" key={sp.id}>
                      <div className="ba-spc-head" onClick={() => toggleExpand(sp.id)}>
                        <span className={"ba-chev" + (exp ? " open" : "")}><svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg></span>
                        <div className="ba-srico"><svg viewBox="0 0 24 24"><path d="M12 2 2 7l10 5 10-5z"/><path d="M2 17l10 5 10-5"/></svg></div>
                        <div className="ba-srm"><div className="n">{sp.name}</div><div className="m">{t("gemeinsam:einheit.instanz", { count: sp.instanceCount || 0 })}{selCount ? t("anlegen.teilen.geteiltSuffix", { anzahl: selCount }) : ""}</div></div>
                        {sel && (
                          <select className="ba-sel" value={sel.role} onClick={e => e.stopPropagation()} onChange={e => setSpaceRole(sp.id, e.target.value)}>
                            <option value="read">{t("anlegen.teilen.nurLesen")}</option>
                            <option value="admin">{t("anlegen.teilen.vollZugriff")}</option>
                          </select>
                        )}
                      </div>
                      {exp && (
                        <div className="ba-spc-body">
                          {!list ? <div className="ba-hint">{t("anlegen.teilen.ladeInstanzen")}</div> : list.length === 0 ? <div className="ba-hint">{t("anlegen.teilen.keineInstanzen")}</div> : (
                            <>
                              <div className="ba-inst" onClick={() => toggleAllInsts(sp.id, list.map(i => i.id))}>
                                <span className={"ba-chk" + (selCount && selCount === list.length ? " on" : "")}><svg viewBox="0 0 24 24"><polyline points="4 12 10 18 20 6"/></svg></span>
                                <span className="ba-inst-n" style={{ fontWeight: 600 }}>{t("anlegen.teilen.alleInstanzen")}</span>
                              </div>
                              {list.map(i => {
                                const on = sel && sel.insts.includes(i.id);
                                return (
                                  <div className="ba-inst" key={i.id} onClick={() => toggleInst(sp.id, i.id)}>
                                    <span className={"ba-chk" + (on ? " on" : "")}><svg viewBox="0 0 24 24"><polyline points="4 12 10 18 20 6"/></svg></span>
                                    <span className="ba-inst-n">{i.name}</span>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="ba-sfoot">{t("anlegen.teilen.fussEinleitung")} <b>{t("anlegen.teilen.nurLesen")}</b> {t("anlegen.teilen.fussLesen")} <b>{t("anlegen.teilen.vollZugriff")}</b> {t("anlegen.teilen.fussVoll")}</div>
              </>
            )}
          </section>
        </div>

        {/* RIGHT: PREVIEW */}
        <aside className="ba-pv">
          <div className="ba-pvtop">
            <div className="gridbg"></div>
            <div className="ba-pvcap">{t("anlegen.vorschau.titel")}</div>
            <div className="ba-pvid">
              <div className={"ba-pvava" + (pop ? " pop" : "")}>{ini}</div>
              <div>
                <div className="ba-pvnm">{name || t("anlegen.vererben.neuerNutzer")}</div>
                <div className="ba-pvun">@{uname.trim() || t("anlegen.vorschau.benutzernamePlatzhalter")}</div>
              </div>
            </div>
            <div className="ba-pvrole"><span className="bar"></span><span>{role === "admin" ? t("anlegen.zugang.rolleAdministrator") : t("anlegen.zugang.rolleNutzer")}</span></div>
          </div>
          <div className="ba-pvbody">
            <div className="ba-sub">{t("anlegen.vorschau.bekommt")}</div>
            <ul className="ba-recv">
              <li className="on">
                <span className="ic"><svg viewBox="0 0 24 24"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg></span>
                <span className="tx">{t("anlegen.vorschau.zugangsdaten")}</span><span className="st">{t("anlegen.vorschau.erstellt")}</span>
              </li>
              <li className={inheritAi && aiConfigured ? "on" : ""}>
                <span className="ic"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg></span>
                <span className="tx">{t("anlegen.vorschau.kiZugang")}</span><span className="st">{inheritAi && aiConfigured ? t("anlegen.vorschau.geerbt") : t("anlegen.vorschau.keiner")}</span>
              </li>
              <li className={inheritAlert && alertConfigured ? "on" : ""}>
                <span className="ic"><svg viewBox="0 0 24 24"><path d="M18 9a6 6 0 0 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 15 18 9z"/><path d="M10.3 20a2 2 0 0 0 3.4 0"/></svg></span>
                <span className="tx">{t("anlegen.vorschau.alerting")}</span><span className="st">{inheritAlert && alertConfigured ? t("anlegen.vorschau.geerbt") : t("anlegen.vorschau.ohne")}</span>
              </li>
              <li className={sharedSpaceCount ? "on" : ""}>
                <span className="ic"><svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 10.5 15.4 6.5M8.6 13.5l6.8 4"/></svg></span>
                <span className="tx">{t("anlegen.vorschau.geteilterZugriff")}</span><span className="st">{sharedSpaceCount ? t("anlegen.vorschau.geteiltZaehler", { spaces: sharedSpaceCount, instanzen: sharedInstCount }) : t("anlegen.vorschau.ohne")}</span>
              </li>
            </ul>
            <div className="ba-cta">
              <button type="button" className="ba-btn prim" onClick={submit} disabled={busy || !valid}>
                <svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>
                {busy ? t("anlegen.vorschau.legtAn") : t("anlegen.vorschau.anlegen")}
              </button>
              <button type="button" className="ba-btn ghost" onClick={() => onDone && onDone()}>{t("gemeinsam:aktion.abbrechen")}</button>
            </div>
          </div>
        </aside>
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
